import { createApi } from '@reduxjs/toolkit/query/react';

import {
  NotificationsEndpoints as Endpoints,
  type GetNotificationSettingsRequest,
  type GetNotificationSettingsResponse,
  type GetNotificationsRequest,
  type GetNotificationsResponse,
  type GetUnreadCountRequest,
  type GetUnreadCountResponse,
  type MarkAllAsReadRequest,
  type MarkAllAsReadResponse,
  type MarkAsReadRequest,
  type MarkAsReadResponse,
  type UpdateNotificationSettingsRequest,
  type UpdateNotificationSettingsResponse,
} from '@app/api/Notifications/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { NOTIFICATIONS_CHUNK } from '@app/constants';

const NOTIFICATIONS_TAG = 'Notifications';
const UNREAD_COUNT_TAG = 'UnreadCount';
const SETTINGS_TAG = 'NotificationSettings';

export const notificationsApi2 = createApi({
  reducerPath: 'notificationsApi2',
  baseQuery: createApiBaseQuery(NOTIFICATIONS_CHUNK),
  tagTypes: [NOTIFICATIONS_TAG, UNREAD_COUNT_TAG, SETTINGS_TAG],
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetNotifications]: query<
      GetNotificationsResponse,
      GetNotificationsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetNotifications,
        method: Methods.GET,
        params,
      }),
      providesTags: [NOTIFICATIONS_TAG],
    }),
    [Endpoints.GetUnreadCount]: query<
      GetUnreadCountResponse,
      GetUnreadCountRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUnreadCount,
        method: Methods.GET,
        params,
      }),
      providesTags: [UNREAD_COUNT_TAG],
    }),
    [Endpoints.MarkAsRead]: mutation<MarkAsReadResponse, MarkAsReadRequest>({
      query: (params) => ({
        url: Endpoints.MarkAsRead,
        method: Methods.POST,
        params,
      }),
      invalidatesTags: [NOTIFICATIONS_TAG, UNREAD_COUNT_TAG],
    }),
    [Endpoints.MarkAllAsRead]: mutation<
      MarkAllAsReadResponse,
      MarkAllAsReadRequest
    >({
      query: (params) => ({
        url: Endpoints.MarkAllAsRead,
        method: Methods.POST,
        params,
      }),
      invalidatesTags: [NOTIFICATIONS_TAG, UNREAD_COUNT_TAG],
    }),
    // Матрица настроек уведомлений «событие × канал». Одиночный свич
    // «Отображение всплывающих уведомлений» — агрегат по каналу in-app,
    // логика в NotificationsBell/lib/popupNotificationsSettings.
    [Endpoints.GetNotificationSettings]: query<
      GetNotificationSettingsResponse,
      GetNotificationSettingsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetNotificationSettings,
        method: Methods.GET,
        params,
      }),
      providesTags: [SETTINGS_TAG],
    }),
    [Endpoints.UpdateNotificationSettings]: mutation<
      UpdateNotificationSettingsResponse,
      UpdateNotificationSettingsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateNotificationSettings,
        method: Methods.POST,
        params,
        data,
      }),
      // Оптимистично подменяем кеш GetNotificationSettings отправляемым
      // значением, чтобы свич не «прыгал» до ответа; при ошибке откат.
      // Тег инвалидируется в любом случае — придёт актуальная матрица.
      onQueryStarted: async (
        { data, ActiveBranchId },
        { dispatch, queryFulfilled }
      ) => {
        const patch = dispatch(
          notificationsApi2.util.updateQueryData(
            Endpoints.GetNotificationSettings,
            { ActiveBranchId },
            () => data
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [SETTINGS_TAG],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
} = notificationsApi2;
